import { Activity, BookOpen, ArrowRight, TrendingUp, Cpu, Users, FileText, Layout } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { useSEO } from '../hooks/useSEO';
import { Link } from 'react-router-dom';
import BreadcrumbsJsonLd from '../components/BreadcrumbsJsonLd';
import { supabase } from '../lib/supabase';
import { useEffect, useState } from 'react';

interface Post {
  id: string;
  slug: string;
  title: string;
  excerpt: string;
  category: string;
  author: string;
  created_at: string;
  type: 'blog' | 'case_study';
  thumbnail_url?: string;
}

export default function Blog() {
  const [posts, setPosts] = useState<Post[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'blog' | 'case_study'>('blog');
  const [hasCaseStudies, setHasCaseStudies] = useState(false);

  useSEO({
    title: 'Blog | AI Hiring Insights & Recruitment Trends - GreenTick',
    description: 'Explore our latest insights on AI recruitment, automated assessment engines, and tips to hire 10x faster with data-backed skill verification.',
    canonicalPath: '/blog'
  });

  useEffect(() => {
    fetchPosts();
  }, []);

  async function fetchPosts() {
    setLoading(true);
    const { data, error } = await supabase
      .from('cms_posts')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching posts:', error);
    } else {
      setPosts(data || []);
      setHasCaseStudies(data?.some(p => p.type === 'case_study') || false);
    }
    setLoading(false);
  }

  const filteredPosts = posts.filter(post => post.type === activeTab);

  const getIcon = (category: string) => {
    const c = category.toLowerCase();
    if (c.includes('tech')) return Cpu;
    if (c.includes('productivity')) return TrendingUp;
    return Users;
  };

  return (
    <div className="min-h-screen bg-slate-950">
      <Navbar />
      <BreadcrumbsJsonLd 
        items={[
          { name: 'Home', item: '/' },
          { name: 'Blog', item: '/blog' }
        ]}
      />
      
      <main className="pt-32 pb-24 home-section-bg">
        {/* Highlighted Grid Pattern */}
        <div className="absolute inset-0 bg-grid-pattern opacity-60 pointer-events-none"></div>
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(20,184,166,0.15),transparent_70%)] pointer-events-none"></div>

        <div className="container mx-auto px-6 relative z-10">
          {/* Header */}
          <div className="max-w-3xl mx-auto text-center mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 glass rounded-full mb-6 border border-teal-500/20">
              <BookOpen className="w-4 h-4 text-teal-400" />
              <span className="text-xs font-bold text-white tracking-widest uppercase">Intellectual Capital</span>
            </div>
            <h1 className="text-5xl font-bold text-white mb-6 uppercase tracking-tighter flex items-center justify-center gap-4">
              The <span className="bg-gradient-to-r from-teal-400 to-emerald-400 bg-clip-text text-transparent">Pulse</span>
              <Activity className="w-10 h-10 text-teal-400 animate-pulse" />
            </h1>
            <p className="text-xl text-slate-400 leading-relaxed font-medium">
              Deep dives into AI recruitment, placement analytics, and the science of high-fidelity skill verification.
            </p>
          </div>

          {/* Tab Switcher (Dynamic) */}
          {hasCaseStudies && (
             <div className="flex justify-center mb-12">
                <div className="glass p-1.5 rounded-2xl flex gap-1 border border-white/10">
                   <button
                     onClick={() => setActiveTab('blog')}
                     className={`px-8 py-3 rounded-xl text-sm font-bold transition-all flex items-center gap-2 ${
                       activeTab === 'blog' ? 'bg-teal-500 text-white shadow-lg shadow-teal-500/20' : 'text-slate-400 hover:text-white'
                     }`}
                   >
                     <Layout className="w-4 h-4" />
                     Blogs
                   </button>
                   <button
                     onClick={() => setActiveTab('case_study')}
                     className={`px-8 py-3 rounded-xl text-sm font-bold transition-all flex items-center gap-2 ${
                       activeTab === 'case_study' ? 'bg-purple-500 text-white shadow-lg shadow-purple-500/20' : 'text-slate-400 hover:text-white'
                     }`}
                   >
                     <FileText className="w-4 h-4" />
                     Case Studies
                   </button>
                </div>
             </div>
          )}

          {/* Blog Grid */}
          {loading ? (
             <div className="text-center py-20 text-slate-400 font-bold uppercase tracking-widest animate-pulse">
                Synchronizing Intelligence...
             </div>
          ) : filteredPosts.length === 0 ? (
             <div className="text-center py-20 glass rounded-3xl border border-white/5">
                <p className="text-slate-400 italic">No {activeTab.replace('_', ' ')} articles published yet.</p>
             </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredPosts.map((post) => {
                const Icon = getIcon(post.category);
                return (
                  <Link
                    key={post.id}
                    to={`/blog/${post.slug}`}
                    className="group relative bg-slate-900 border border-white/10 rounded-3xl p-8 hover:border-teal-500/50 transition-all duration-500 hover:-translate-y-2 flex flex-col h-full shadow-2xl"
                  >
                    <div className="absolute inset-0 bg-gradient-to-br from-teal-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity rounded-3xl"></div>
                    
                    <div className="w-14 h-14 rounded-2xl bg-teal-500/10 flex items-center justify-center mb-6 border border-teal-500/20 group-hover:scale-110 group-hover:bg-teal-500/20 transition-all">
                      <Icon className="w-7 h-7 text-teal-400" />
                    </div>

                    <div className="flex items-center gap-4 mb-4 relative z-10">
                      <span className="px-3 py-1 bg-teal-500/10 text-teal-400 text-[10px] font-bold uppercase tracking-wider rounded-lg border border-teal-500/20">
                        {post.category}
                      </span>
                      <span className="text-[10px] text-slate-400 uppercase tracking-widest font-bold">
                        {new Date(post.created_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
                      </span>
                    </div>

                    <h3 className="text-2xl font-bold text-white mb-4 group-hover:text-teal-400 transition-colors uppercase tracking-tight leading-none">
                      {post.title}
                    </h3>
                    
                    <p className="text-slate-400 leading-relaxed mb-8 flex-1 font-medium italic text-sm">
                      {post.excerpt}
                    </p>

                    <div className="flex items-center justify-between pt-6 border-t border-white/5 mt-auto relative z-10">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-slate-800 border border-white/10 flex items-center justify-center text-[10px] text-white font-bold uppercase">
                          {post.author.charAt(0)}
                        </div>
                        <span className="text-xs text-slate-400 font-bold uppercase tracking-wider">{post.author}</span>
                      </div>
                      <ArrowRight className="w-5 h-5 text-teal-500 -translate-x-2 opacity-0 group-hover:translate-x-0 group-hover:opacity-100 transition-all" />
                    </div>
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}
